/**
 * PropCard.tsx
 * Individual prop/objective card with stat target display
 * 
 * V6 POLISH: Added FAVORABLE/TOUGH indicator based on Season Avg vs Line
 * 
 * Single Responsibility: Display single prop target, emit selection event
 * Props In: prop, rivals
 * Events Out: onSelect(prop)
 */
import React from 'react';
import { ArrowRight } from 'lucide-react';
import { RivalScouts } from './RivalScouts';

export interface Prop {
  id: number;
  statType: string;
  line: number;
  seasonAvg?: number;
  last3?: number[];
  defRank?: number;
  defLabel?: string;
}

interface PropCardProps {
  prop: Prop;
  rivals: string[];
  onSelect: (prop: Prop) => void;
}

export const PropCard: React.FC<PropCardProps> = ({
  prop,
  rivals,
  onSelect,
}) => {
  // Calculate edge for FAVORABLE/TOUGH display
  const edge = prop.seasonAvg ? prop.seasonAvg - prop.line : null;
  const isFavorable = edge !== null && edge > 0;

  return (
    <div className="snap-center shrink-0 w-[85vw] max-w-[340px]">
      <div className="bg-gradient-to-b from-slate-800/90 to-slate-900/90 backdrop-blur-xl rounded-3xl overflow-hidden border border-slate-700/50 shadow-2xl relative">
        <div className="p-5">
          {/* Stat Type Header */}
          <div className="mb-4">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Stat Target</p>
            <p className="text-lg font-bold text-white">{prop.statType}</p>
          </div>

          {/* Rival Scouts (GR-1) */}
          {rivals.length > 0 && (
            <div className="mb-4">
              <RivalScouts rivals={rivals} action="targeting" />
            </div>
          )}

          {/* Target Line - Main Focus */}
          <div className="bg-slate-800/50 rounded-2xl p-5 mb-4 border border-slate-700/30">
            <div className="text-center">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">Target Line</p>
              <div className="font-mono text-5xl font-black text-white tracking-tight">
                {prop.line}
              </div>
            </div>
          </div>

          {/* Stats Context with FAVORABLE/TOUGH */}
          {prop.seasonAvg && (
            <div className="flex items-center justify-between mb-4 px-1">
              <span className="text-[11px] text-slate-500">
                Season Avg: <span className="text-slate-300 font-mono">{prop.seasonAvg}</span>
              </span>
              
              {/* FAVORABLE/TOUGH indicator */}
              {edge !== null && (
                <span className={`text-xs font-bold ${isFavorable ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {isFavorable ? 'FAVORABLE' : 'TOUGH'} {isFavorable ? '+' : ''}{edge.toFixed(1)}
                </span>
              )}
            </div>
          )}

          {/* Last 3 (if available, separate row) */}
          {prop.last3 && (
            <div className="text-[11px] text-slate-500 mb-4 px-1">
              <span>Last 3: <span className="text-slate-300 font-mono">{prop.last3.join(', ')}</span></span>
            </div>
          )}

          {/* Select Button - Amber to match PlayerCard */}
          <button 
            onClick={() => onSelect(prop)}
            className="w-full py-4 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-slate-900 font-bold text-base rounded-xl shadow-lg shadow-amber-500/25 transition-all active:scale-[0.98] flex items-center justify-center gap-2 min-h-[48px]"
          >
            SELECT TARGET
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropCard;
